import React, { useState } from 'react';
import ReactLoading from 'react-loading';
import { FormErrorMessage, FormField, FormLabelWrapper } from 'components/common/form-utils/form-elements';
import { FormState, useForm } from 'components/common/form-utils/form-state';
import { Product } from 'utils/Product';
import ProductViewer from '../ProductViewer';
import { Button } from 'components/common/Button';
import { validateForm, validateFormField, Validators } from 'components/common/form-utils/validators';
import CreatableSelect from 'react-select/creatable';
import { convertFileToBase64 } from 'utils/image';
import Rating from 'components/common/Rating';
import { Icon } from 'components/common/Icon';
import { checkCircleIcon } from 'assets/icons';
import { requests } from 'api-services';

type ProductUnderConstruction = Partial<Product>;

const ProductCreatorPage = () => {
  const [state, setState] = useState<'ready' | 'creating' | 'error' | 'done'>('ready');
  const [categories, setCategories] = useState(['Stuffed Toys']);

  const defaultProductDisplay: Product = {
    title: 'Title',
    category: 'Category',
    id: '',
    image: '',
    stars: 0,
    price: 0,
    quantity: 0,
    visible: false,
  };

  const form: FormState = useForm({
    title: { visibleName: 'Title', required: true },
    category: { visibleName: 'Category', required: true },
    image: { visibleName: 'Image', required: true },
    stars: {
      visibleName: 'Stars',
      required: true,
      validations: [Validators.isInteger(), Validators.minValue(1), Validators.maxValue(5)],
    },
    price: { visibleName: 'Price', required: true, validations: [Validators.isNumber(), Validators.minValue(0)] },
    quantity: { visibleName: 'Quantity', required: true, validations: [Validators.isInteger(), Validators.minValue(0)] },
    visible: { visibleName: 'Visible', type: 'boolean' },
  });

  const getProduct = (): ProductUnderConstruction => {
    const product: ProductUnderConstruction = {};
    if (form.title.value && !form.title.error) product.title = form.title.value;
    if (form.category.value && !form.category.error) product.category = form.category.value;
    if (form.image.value && !form.image.error) product.image = form.image.value;
    if (form.stars.value && !form.stars.error) product.stars = Number.parseInt(form.stars.value);
    if (form.price.value && !form.price.error) product.price = Number.parseFloat(form.price.value);
    if (form.quantity.value && !form.quantity.error) product.quantity = Number.parseInt(form.quantity.value);
    if (form.visible.value && !form.visible.error) product.visible = form.visible.getBoolean();
    return product;
  };

  const getProductView = () => ({
    ...defaultProductDisplay,
    ...getProduct(),
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm(form)) return;

    setState('creating');
    const result = getProduct() as Product;

    requests.product.post(result)
      .then(() => setState('done'))
      .catch(() => setState('error'));
  };

  return <div className='my-8'>
    <div className='text-2xl pb-4 font-medium'>Create Product</div>
    <div className='flex justify-center flex-row py-8'>
      <div className='w-120 font-medium pr-8'>
        <ProductViewer product={getProductView()} name='Your Product' id='product-view' />
      </div>
      {state === 'creating' && <ReactLoading className='m-auto' type={'bubbles'} color={'#ac542e'} height={'50%'} width={'8%'} />}
      {state === 'error' && <span>An error occurred creating the product.</span>}
      {state === 'done' && <span>Product {form.title.value} created.</span>}
      {state === 'ready'
        && <form onSubmit={onSubmit}>
          <FormField hideRequiredStar validateOnChange field={form.title} />
          <FormLabelWrapper name='Category' id='category-label' input={
            <div className='w-full'>
              <CreatableSelect
                id='product-category'
                aria-labelledby="category-label"
                className='w-48'
                isClearable
                onChange={e => {
                  if (!e) return;
                  form.category.setValue(e.value);
                  if (!categories.includes(e.value)) setCategories([...categories, e.value]);
                  validateFormField(form.category, e.value);
                }}
                options={categories.map(c => ({ value: c, label: c }))}
              />
              {form.category.error && <FormErrorMessage>{form.category.error}</FormErrorMessage>}
            </div>
          } />
          <FormLabelWrapper name='Image' htmlFor='image-upload' input={
            <div className='w-full'>
              <span className='inline w-full'>
                <input type='file' id='image-upload' onChange={
                  event => {
                    event.target.files
                      && convertFileToBase64(event.target.files[0] as File)
                        .then(val => {
                          form.image.setValue(val as string);
                          validateFormField(form.image, val as string);
                        });
                  }} />
                {form.image.value && <span className='absolute p-2'>
                  <Icon label='Image uploaded successfully' src={checkCircleIcon} />
                </span>}
              </span>
              {form.image.error && <div>
                <FormErrorMessage>{form.image.error}</FormErrorMessage>
              </div>}
            </div>
          } />
          <FormLabelWrapper name='Stars' id='stars-label' input={
            <div className='w-full h-full align-middle'>
              <div className='h-full align-middle' aria-labelledby='stars-label'>
                <Rating
                  label='Set the rating for this product'
                  size={20}
                  ratingValue={form.stars.value ? Number.parseInt(form.stars.value) : 0}
                  onClick={index => {
                    form.stars.setValue(index.toString());
                    validateFormField(form.stars, index.toString());
                  }} />
              </div>
              {form.stars.error && <div><FormErrorMessage>{form.stars.error}</FormErrorMessage></div>}
            </div>
          } />
          <FormField hideRequiredStar validateOnChange field={form.price} />
          <FormField hideRequiredStar validateOnChange field={form.quantity} />
          <FormLabelWrapper name='Visible' htmlFor='product-visibility' input={
            <div className='w-full'>
              <input
                type="checkbox"
                id='product-visibility'
                onChange={e => {
                  form.visible.setBoolean(e.target.checked);
                }}
                className="form-checkbox mt-2" />
            </div>
          } />
          <div className='flex flex-row w-full place-content-end space-x-1 pr-4'>
            <Button grey id='cancel-button' linkTo='/admin/products'>Cancel</Button>
            <Button primary id='create-button' className='w-32' type='submit'>Create Product</Button>
          </div>
        </form>}
    </div>
  </div>;
};

export default ProductCreatorPage;
